/**
 * Theme gallery.
 *
 * A theme bundles typography, background, accent colours, contrast,
 * default transition, text styles, spacing and slide layouts. Applying a
 * theme never deletes content: it sets the slide background + transition
 * and restyles text font / colour in place (sizes and wording are kept).
 */

import { allowCanvasFont } from "../Components/TextElement.tsx";

export const THEMES = [
  {
    id: "medical-cinema",
    name: "Medical Cinema",
    description: "Dark navy, blue lighting, elegant typography",
    tone: "dark",
    background: { kind: "preset", presetId: "atmospheric-glow" },
    fonts: { heading: "Playfair Display", body: "Inter" },
    colors: { heading: "#f1f5ff", text: "#cdd8ee", muted: "#8fa3c4", accent: "#4fa3ff", accent2: "#7dd3fc" },
    transition: { type: "zoom", duration: 750, easing: "cinematic" },
    styles: { heading: { size: 58, weight: 600 }, subheading: { size: 30, weight: 500 }, body: { size: 22, weight: 400 } },
    spacing: { margin: 96, gap: 28 },
    layouts: {
      title: { heading: { left: 96, top: 250, width: 1000, height: 110 }, sub: { left: 98, top: 372, width: 820, height: 60 } },
      content: { heading: { left: 96, top: 72, width: 1088, height: 80 }, body: { left: 96, top: 184, width: 560, height: 420 }, media: { left: 700, top: 160, width: 484, height: 470 } },
    },
  },
  {
    id: "apple-minimal",
    name: "Apple Minimal",
    description: "White space, quiet type, minimal motion",
    tone: "light",
    background: { kind: "preset", presetId: "minimal-white" },
    fonts: { heading: "Inter", body: "Inter" },
    colors: { heading: "#111114", text: "#3a3a40", muted: "#86868b", accent: "#0071e3", accent2: "#5e5ce6" },
    transition: { type: "fade", duration: 600, easing: "smooth" },
    styles: { heading: { size: 64, weight: 700 }, subheading: { size: 30, weight: 500 }, body: { size: 22, weight: 400 } },
    spacing: { margin: 120, gap: 32 },
    layouts: {
      title: { heading: { left: 140, top: 260, width: 1000, height: 110 }, sub: { left: 140, top: 380, width: 1000, height: 60 } },
      content: { heading: { left: 120, top: 90, width: 1040, height: 90 }, body: { left: 120, top: 210, width: 500, height: 400 }, media: { left: 670, top: 190, width: 490, height: 430 } },
    },
  },
  {
    id: "scientific-dark",
    name: "Scientific Dark",
    description: "Charcoal, cyan accents, high contrast",
    tone: "dark",
    background: { kind: "preset", presetId: "graphite-black" },
    fonts: { heading: "IBM Plex Sans", body: "IBM Plex Sans" },
    colors: { heading: "#ffffff", text: "#d4d4d8", muted: "#a1a1aa", accent: "#22d3ee", accent2: "#a3e635" },
    transition: { type: "blur", duration: 650, easing: "easeInOut" },
    styles: { heading: { size: 52, weight: 600 }, subheading: { size: 28, weight: 500 }, body: { size: 21, weight: 400 } },
    spacing: { margin: 80, gap: 24 },
    layouts: {
      title: { heading: { left: 80, top: 240, width: 1100, height: 110 }, sub: { left: 82, top: 360, width: 900, height: 60 } },
      content: { heading: { left: 80, top: 64, width: 1120, height: 80 }, body: { left: 80, top: 176, width: 520, height: 460 }, media: { left: 640, top: 160, width: 560, height: 480 } },
    },
  },
  {
    id: "aurora-glass",
    name: "Aurora Glass",
    description: "Soft aurora gradient with restrained glass",
    tone: "dark",
    background: { kind: "preset", presetId: "animated-aurora" },
    fonts: { heading: "Manrope", body: "Manrope" },
    colors: { heading: "#ffffff", text: "#e2e8f0", muted: "#a5b4cb", accent: "#a78bfa", accent2: "#5eead4" },
    transition: { type: "depth", duration: 800, easing: "smooth" },
    styles: { heading: { size: 56, weight: 700 }, subheading: { size: 30, weight: 500 }, body: { size: 22, weight: 400 } },
    spacing: { margin: 96, gap: 28 },
    layouts: {
      title: { heading: { left: 96, top: 240, width: 1088, height: 120 }, sub: { left: 98, top: 370, width: 880, height: 60 } },
      content: { heading: { left: 96, top: 72, width: 1088, height: 80 }, body: { left: 96, top: 184, width: 540, height: 430 }, media: { left: 680, top: 168, width: 504, height: 460 } },
    },
  },
  {
    id: "academic-professional",
    name: "Academic Professional",
    description: "Clean, university-friendly design",
    tone: "light",
    background: { kind: "preset", presetId: "medical-grid" },
    fonts: { heading: "Source Serif 4", body: "Source Sans 3" },
    colors: { heading: "#0b2545", text: "#1f2d3d", muted: "#5b6b7f", accent: "#13315c", accent2: "#8da9c4" },
    transition: { type: "reveal", duration: 600, easing: "easeInOut" },
    styles: { heading: { size: 50, weight: 700 }, subheading: { size: 28, weight: 600 }, body: { size: 22, weight: 400 } },
    spacing: { margin: 88, gap: 24 },
    layouts: {
      title: { heading: { left: 88, top: 250, width: 1100, height: 110 }, sub: { left: 90, top: 366, width: 900, height: 60 } },
      content: { heading: { left: 88, top: 64, width: 1104, height: 80 }, body: { left: 88, top: 176, width: 540, height: 460 }, media: { left: 668, top: 168, width: 524, height: 470 } },
    },
  },
  {
    id: "nature-biology",
    name: "Nature & Biology",
    description: "Organic greens and soft blue gradients",
    tone: "dark",
    background: { kind: "preset", presetId: "dark-emerald" },
    fonts: { heading: "Lora", body: "Nunito Sans" },
    colors: { heading: "#ecfdf5", text: "#d1fae5", muted: "#8cc7ad", accent: "#34d399", accent2: "#67e8f9" },
    transition: { type: "parallax", duration: 800, easing: "smooth" },
    styles: { heading: { size: 54, weight: 600 }, subheading: { size: 30, weight: 500 }, body: { size: 22, weight: 400 } },
    spacing: { margin: 96, gap: 28 },
    layouts: {
      title: { heading: { left: 96, top: 250, width: 1088, height: 110 }, sub: { left: 98, top: 370, width: 880, height: 60 } },
      content: { heading: { left: 96, top: 72, width: 1088, height: 80 }, body: { left: 96, top: 184, width: 540, height: 430 }, media: { left: 680, top: 168, width: 504, height: 460 } },
    },
  },
  {
    id: "premium-black",
    name: "Premium Black",
    description: "Black canvas, sophisticated typography",
    tone: "dark",
    background: { kind: "preset", presetId: "cinematic-spotlight" },
    fonts: { heading: "DM Serif Display", body: "Inter" },
    colors: { heading: "#fafafa", text: "#d4d4d4", muted: "#8a8a8a", accent: "#d4af37", accent2: "#e5e5e5" },
    transition: { type: "film", duration: 900, easing: "cinematic" },
    styles: { heading: { size: 62, weight: 400 }, subheading: { size: 28, weight: 400 }, body: { size: 21, weight: 400 } },
    spacing: { margin: 110, gap: 30 },
    layouts: {
      title: { heading: { left: 110, top: 240, width: 1060, height: 120 }, sub: { left: 112, top: 376, width: 860, height: 60 } },
      content: { heading: { left: 110, top: 80, width: 1060, height: 84 }, body: { left: 110, top: 196, width: 500, height: 420 }, media: { left: 660, top: 176, width: 510, height: 450 } },
    },
  },
  {
    id: "modern-editorial",
    name: "Modern Editorial",
    description: "Large headings, asymmetry, refined spacing",
    tone: "light",
    background: { kind: "solid", color: "#f5f1ea" },
    fonts: { heading: "Fraunces", body: "Inter" },
    colors: { heading: "#1a1a1a", text: "#33302b", muted: "#7a736a", accent: "#c2410c", accent2: "#1e3a8a" },
    transition: { type: "pan", duration: 750, easing: "easeInOut" },
    styles: { heading: { size: 72, weight: 600 }, subheading: { size: 30, weight: 500 }, body: { size: 21, weight: 400 } },
    spacing: { margin: 72, gap: 36 },
    layouts: {
      title: { heading: { left: 72, top: 300, width: 900, height: 150 }, sub: { left: 76, top: 470, width: 640, height: 60 } },
      content: { heading: { left: 72, top: 60, width: 760, height: 150 }, body: { left: 860, top: 120, width: 348, height: 520 }, media: { left: 72, top: 240, width: 740, height: 420 } },
    },
  },
];

export const getTheme = (id) => THEMES.find((t) => t.id === id) || null;

const GOOGLE_FONT_FAMILIES = {
  "Playfair Display": "Playfair+Display:wght@400;500;600;700",
  Inter: "Inter:wght@300;400;500;600;700",
  "IBM Plex Sans": "IBM+Plex+Sans:wght@400;500;600;700",
  Manrope: "Manrope:wght@400;500;600;700",
  "Source Serif 4": "Source+Serif+4:wght@400;600;700",
  "Source Sans 3": "Source+Sans+3:wght@400;600;700",
  Lora: "Lora:wght@400;500;600;700",
  "Nunito Sans": "Nunito+Sans:wght@400;600;700",
  "DM Serif Display": "DM+Serif+Display",
  Fraunces: "Fraunces:wght@400;600;700",
};

const loadedFonts = new Set();

/** Lazily load the web fonts for a theme and allow them in the text editor. */
export const ensureThemeFonts = (theme) => {
  if (!theme || typeof document === "undefined") return;
  const families = [theme.fonts.heading, theme.fonts.body].filter(
    (f) => f && GOOGLE_FONT_FAMILIES[f] && !loadedFonts.has(f)
  );
  [theme.fonts.heading, theme.fonts.body].forEach((f) => f && allowCanvasFont(f));
  if (!families.length) return;
  families.forEach((f) => loadedFonts.add(f));
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${families
    .map((f) => `family=${GOOGLE_FONT_FAMILIES[f]}`)
    .join("&")}&display=swap`;
  document.head.appendChild(link);
};

/** Load fonts for every theme referenced by a set of slides (viewer pages). */
export const ensureFontsForThemeIds = (ids) => {
  (ids || []).forEach((id) => ensureThemeFonts(getTheme(id)));
};

/* -------------------------- Text restyling ------------------------------ */

const sizeOf = (style) => {
  const m = /font-size:\s*([\d.]+)px/i.exec(style || "");
  return m ? parseFloat(m[1]) : null;
};

/** Guess a text box's role from its largest font size. */
export const textRole = (html) => {
  const sizes = [];
  String(html || "").replace(/font-size:\s*([\d.]+)px/gi, (_, n) => {
    sizes.push(parseFloat(n));
    return _;
  });
  const max = sizes.length ? Math.max(...sizes) : 0;
  if (/<h1[\s>]/i.test(html) || max >= 44) return "heading";
  if (/<h2[\s>]/i.test(html) || max >= 28) return "subheading";
  return "body";
};

const setStyleProp = (style, prop, value) => {
  const re = new RegExp(`(^|;)\\s*${prop}\\s*:[^;]*`, "i");
  if (re.test(style)) return style.replace(re, `$1 ${prop}: ${value}`);
  const trimmed = style.trim();
  return `${trimmed}${trimmed && !trimmed.endsWith(";") ? ";" : ""} ${prop}: ${value};`.trim();
};

/**
 * Restyle a Quill HTML string for a theme. Only font-family and colour are
 * touched; wording, sizes, bold/italic and layout are preserved. Background
 * highlight colours are left alone.
 */
export const restyleTextHtml = (html, theme) => {
  if (!theme || typeof html !== "string" || !html) return html;
  const role = textRole(html);
  const font = role === "body" ? theme.fonts.body : theme.fonts.heading;
  const color = role === "heading" ? theme.colors.heading : role === "subheading" ? theme.colors.heading : theme.colors.text;

  let touched = false;
  const out = html.replace(/style="([^"]*)"/gi, (match, style) => {
    // Inline highlights / buttons keep their author colours.
    if (/background-color/i.test(style) && !/font-size/i.test(style)) return match;
    let next = style.replace(/&quot;/g, "'");
    if (/font-family/i.test(next) || sizeOf(next) != null) {
      next = setStyleProp(next, "font-family", font);
    }
    if (/(^|;)\s*color\s*:/i.test(next) || sizeOf(next) != null) {
      next = setStyleProp(next, "color", color);
    }
    touched = true;
    return `style="${next.replace(/'/g, "&quot;")}"`;
  });
  if (touched) return out;
  // Plain text with no inline styles: wrap paragraphs' content in a styled span.
  return html.replace(
    /<p([^>]*)>([\s\S]*?)<\/p>/gi,
    (m, attrs, inner) =>
      inner.trim() ? `<p${attrs}><span style="font-family: ${font}; color: ${color};">${inner}</span></p>` : m
  );
};

/** Build styled HTML for a new text box in the current theme. */
export const themedTextHtml = (theme, role, text) => {
  const style = theme?.styles?.[role] || { size: role === "heading" ? 58 : role === "subheading" ? 34 : 24, weight: 400 };
  const font = theme ? (role === "body" ? theme.fonts.body : theme.fonts.heading) : "Segoe UI, Arial, sans-serif";
  const color = theme ? (role === "body" ? theme.colors.text : theme.colors.heading) : "rgb(15, 23, 42)";
  const tag = style.weight >= 600 ? "strong" : "span";
  return `<p><${tag} style="font-size: ${style.size}px; font-family: ${font}; color: ${color};">${text}</${tag}></p>`;
};
